import { Button } from "@/admin/ui";

/**
 * Reusable template every role dashboard uses.
 * config: { title, eyebrow, purpose, actions[], cards[], sections[] }
 */
export default function DashboardTemplate({ config, extraTop, extraBottom }) {
    return (
        <div data-testid="dashboard-page">
            <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
                <div className="max-w-3xl">
                    {config.eyebrow && (
                        <div className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                            <span className="h-px w-6 bg-neutral-300" />
                            {config.eyebrow}
                        </div>
                    )}
                    <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-black">
                        {config.title}
                    </h1>
                    {config.purpose && (
                        <p className="mt-3 text-[14px] sm:text-[15px] text-neutral-600 leading-relaxed">
                            {config.purpose}
                        </p>
                    )}
                </div>
                {config.actions?.length ? (
                    <div className="flex flex-wrap gap-2">
                        {config.actions.map((a, i) => (
                            <ActionButton key={i} action={a} />
                        ))}
                    </div>
                ) : null}
            </header>

            {extraTop}

            {config.cards?.length ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
                    {config.cards.map((c, i) => (
                        <KpiCard key={i} card={c} i={i} />
                    ))}
                </div>
            ) : null}

            {config.sections?.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {config.sections.map((s, i) => (
                        <SectionCard key={i} section={s} i={i} />
                    ))}
                </div>
            ) : null}

            {extraBottom}
        </div>
    );
}

function ActionButton({ action }) {
    const { label, primary, locked } = action;
    if (locked) {
        return (
            <button
                type="button"
                onClick={() => alert(`${label} — coming soon.`)}
                data-testid={`dash-action-${slug(label)}`}
                className="inline-flex items-center gap-1.5 rounded-full font-medium text-[13px] px-4 py-2 bg-white text-neutral-600 border border-black/10 hover:bg-neutral-50 transition-all"
            >
                <LockIcon />
                {label}
            </button>
        );
    }
    return (
        <Button
            variant={primary ? "primary" : "ghost"}
            size="md"
            onClick={() => alert(`${label} — placeholder in this phase.`)}
            data-testid={`dash-action-${slug(label)}`}
        >
            {label}
            {primary && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
        </Button>
    );
}

function KpiCard({ card, i }) {
    const toneCls =
        card.tone === "positive"
            ? "text-emerald-600"
            : card.tone === "warning"
                ? "text-amber-600"
                : "text-black";
    if (card.locked) {
        return (
            <div
                data-testid={`kpi-${slug(card.label)}`}
                className="relative glass rounded-2xl p-5 overflow-hidden"
            >
                <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 100% at 100% 0%, rgba(139,92,246,0.08), transparent 60%)" }} />
                <div className="relative">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-neutral-500">{card.label}</span>
                        <LockPill />
                    </div>
                    <div className="mt-3 font-display text-2xl font-medium text-neutral-400 leading-none">{card.value}</div>
                    <div className="mt-1.5 text-[11px] text-neutral-400">{card.hint || "Coming soon"}</div>
                </div>
            </div>
        );
    }
    return (
        <div
            data-testid={`kpi-${slug(card.label)}`}
            className="glass rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-20px_rgba(139,92,246,0.28)] transition-all duration-500"
            style={{ transitionDelay: `${i * 40}ms` }}
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-neutral-500">{card.label}</span>
                <span className="font-mono-alt text-[10px] text-violet-500">/{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className={`mt-3 font-display text-2xl font-medium leading-none ${toneCls}`}>
                {card.value}
            </div>
            {card.hint && <div className="mt-1.5 text-[11px] text-neutral-500">{card.hint}</div>}
        </div>
    );
}

function SectionCard({ section, i }) {
    return (
        <div
            data-testid={`section-${slug(section.title)}`}
            className="glass rounded-2xl p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-20px_rgba(139,92,246,0.25)] transition-all duration-500"
            style={{ transitionDelay: `${i * 30}ms` }}
        >
            <div className="flex items-center justify-between">
                <span className="font-mono-alt text-[10px] text-neutral-400">
                    /{String(i + 1).padStart(2, "0")}
                </span>
                {section.locked && <LockPill />}
            </div>
            <div className="mt-4 font-display text-[19px] font-medium tracking-tight text-black">
                {section.title}
            </div>
            {section.body && (
                <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">{section.body}</p>
            )}
            <div className="mt-5 flex items-center gap-2 text-[12px]">
                <button
                    onClick={() =>
                        alert(
                            section.locked
                                ? `${section.title} — coming soon.`
                                : `Open ${section.title} — placeholder in this phase.`,
                        )
                    }
                    className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-700"
                >
                    {section.locked ? "Preview" : "Open"} →
                </button>
            </div>
        </div>
    );
}

function LockPill() {
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">
            <LockIcon /> Soon
        </span>
    );
}

function LockIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    );
}

function slug(s) {
    return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
